import {
  addDays,
  addMonths,
  addWeeks,
  differenceInCalendarDays,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
} from "date-fns";
import { ru } from "date-fns/locale/ru";
import type { DateRangeValue, PeriodPreset } from "components/PeriodSelector/PeriodSelector";
import type { StatisticResponse } from "services/statisticService/statisticService.types";
import type {
  StatisticMetricId,
  StatisticsBucket,
  StatisticsGranularity,
} from "./StatisticsPage.types";

const NUMBER_FORMAT = new Intl.NumberFormat("ru-RU");
const MONEY_FORMAT = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 });
const pointDate = (point: StatisticResponse) => parseISO(point.created);

export const getEarliestStatisticDate = (data: StatisticResponse[]): Date | null => {
  if (!data.length) return null;
  return data.reduce<Date>((earliest, point) => {
    const current = pointDate(point);
    return isBefore(current, earliest) ? current : earliest;
  }, pointDate(data[0]));
};

export const getPresetRange = (
  preset: Exclude<PeriodPreset, "custom">,
  earliest: Date | null,
  now: Date = new Date(),
): DateRangeValue => {
  const to = startOfDay(now);
  if (preset === "all") return { from: startOfDay(earliest ?? now), to };
  const days = Number(preset);
  return { from: subDays(to, Math.max(days - 1, 0)), to };
};

export const getGranularity = (range: DateRangeValue): StatisticsGranularity => {
  const span = differenceInCalendarDays(range.to, range.from) + 1;
  if (span <= 45) return "day";
  if (span <= 200) return "week";
  return "month";
};

const labelFor = (date: Date, granularity: StatisticsGranularity) =>
  format(date, granularity === "month" ? "MMM yy" : "dd MMM", { locale: ru }).replace(/\./g, "");

const latestTotalUsersAt = (data: StatisticResponse[], date: Date): number => {
  const cutoff = endOfDay(date).getTime();
  return data.reduce((latest, point) => {
    const timestamp = pointDate(point).getTime();
    if (timestamp <= cutoff && timestamp >= latest.timestamp) {
      return { timestamp, value: point.total_users };
    }
    return latest;
  }, { timestamp: Number.NEGATIVE_INFINITY, value: 0 }).value;
};

export const aggregateStatistics = (
  data: StatisticResponse[],
  range: DateRangeValue,
): { granularity: StatisticsGranularity; buckets: StatisticsBucket[] } => {
  const normalizedRange = { from: startOfDay(range.from), to: startOfDay(range.to) };
  const granularity = getGranularity(normalizedRange);
  const buckets: StatisticsBucket[] = [];
  let cursor = granularity === "day"
    ? normalizedRange.from
    : granularity === "week"
      ? startOfWeek(normalizedRange.from, { weekStartsOn: 1 })
      : startOfMonth(normalizedRange.from);

  while (!isAfter(cursor, normalizedRange.to)) {
    const naturalEnd = granularity === "day"
      ? cursor
      : granularity === "week"
        ? endOfWeek(cursor, { weekStartsOn: 1 })
        : endOfMonth(cursor);
    const bucketStart = isBefore(cursor, normalizedRange.from) ? normalizedRange.from : cursor;
    const bucketEnd = isAfter(naturalEnd, normalizedRange.to) ? normalizedRange.to : naturalEnd;
    const points = data.filter((point) => {
      const date = pointDate(point);
      return !isBefore(date, bucketStart) && !isAfter(date, endOfDay(bucketEnd));
    });

    buckets.push({
      key: format(cursor, "yyyy-MM-dd"),
      label: labelFor(bucketStart, granularity),
      totalIncome: points.reduce((sum, point) => sum + point.total_income, 0),
      newUsers: points.reduce((sum, point) => sum + point.new_users, 0),
      subscriptionDays: points.reduce((sum, point) => sum + point.subscription_days, 0),
      totalUsers: latestTotalUsersAt(data, bucketEnd),
    });

    cursor = granularity === "day"
      ? addDays(cursor, 1)
      : granularity === "week"
        ? addWeeks(cursor, 1)
        : addMonths(cursor, 1);
  }

  return { granularity, buckets };
};

const inRange = (point: StatisticResponse, range: DateRangeValue) => {
  const date = pointDate(point);
  return !isBefore(date, startOfDay(range.from)) && !isAfter(date, endOfDay(range.to));
};

export const getMetricValue = (
  metric: StatisticMetricId,
  data: StatisticResponse[],
  range: DateRangeValue,
): number => {
  const points = data.filter((point) => inRange(point, range));
  if (metric === "income") return points.reduce((sum, point) => sum + point.total_income, 0);
  if (metric === "joined") return points.reduce((sum, point) => sum + point.new_users, 0);
  if (metric === "days") return points.reduce((sum, point) => sum + point.subscription_days, 0);
  return latestTotalUsersAt(data, range.to);
};

export const getPreviousRange = (range: DateRangeValue): DateRangeValue => {
  const days = differenceInCalendarDays(range.to, range.from) + 1;
  const to = subDays(startOfDay(range.from), 1);
  return { from: subDays(to, Math.max(days - 1, 0)), to };
};

export const getMetricDelta = (
  metric: StatisticMetricId,
  data: StatisticResponse[],
  range: DateRangeValue,
): number | null => {
  const previousRange = getPreviousRange(range);
  const current = metric === "total"
    ? getMetricValue("joined", data, range)
    : getMetricValue(metric, data, range);
  const previous = metric === "total"
    ? getMetricValue("joined", data, previousRange)
    : getMetricValue(metric, data, previousRange);
  if (previous === 0) return null;
  return Math.round(((current - previous) / Math.abs(previous)) * 100);
};

export const formatMetricValue = (metric: StatisticMetricId, value: number): string => {
  if (metric === "income") return `${MONEY_FORMAT.format(Math.round(value))} руб.`;
  if (metric === "joined" || metric === "total") return `${NUMBER_FORMAT.format(Math.round(value))} чел.`;
  return NUMBER_FORMAT.format(Math.round(value));
};

export const formatMetricAxisValue = (metric: StatisticMetricId, value: number): string => {
  const absolute = Math.abs(value);
  if (metric === "income") {
    if (absolute >= 1_000_000) return `${(value / 1_000_000).toFixed(1)} млн`;
    if (absolute >= 1_000) return `${Math.round(value / 1_000)} тыс.`;
  }
  return NUMBER_FORMAT.format(Math.round(value));
};

export const formatPeriodNote = (range: DateRangeValue, granularity: StatisticsGranularity) => {
  const label = granularity === "day" ? "по дням" : granularity === "week" ? "по неделям" : "по месяцам";
  return `Период: ${format(range.from, "dd.MM.yyyy")} - ${format(range.to, "dd.MM.yyyy")} · ${label}`;
};
